import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from './firebase';
import { 
  collection, addDoc, updateDoc, deleteDoc, 
  doc, query, orderBy, onSnapshot, serverTimestamp 
} from 'firebase/firestore';
import { motion, AnimatePresence, } from 'framer-motion';
import './BucketList.css';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Audio Assets
import createSound from './assets/create.mp3';
import deleteSound from './assets/delete.mp3';
import magicSound from './assets/magic.mp3';

const SIGNALS = ["DREAM", "TRAVEL", "SELF", "GROW", "LOVE", "WORK"];

export default function BucketList() {
  const [items, setItems] = useState([]);
  const [input, setInput] = useState('');
  const [selectedSignal, setSelectedSignal] = useState("DREAM");
  const [editingId, setEditingId] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState([]);
const [isGenerating, setIsGenerating] = useState(false);
const [editText, setEditText] = useState('');
   const [showLimitModal, setShowLimitModal] = useState(false);
  const audioCreate = useRef(new Audio(createSound));
  const audioDelete = useRef(new Audio(deleteSound));
    const magicAudio = useRef(new Audio(magicSound));
 const LimitModal = ({ isOpen, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <div className="modal-overlay">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="limit-modal-content"
        >
          <div className="modal-icon">⏳</div>
          <h2>Get your brains work🥀</h2>
          <p>
            Write your own bucket list. Our AI needs some rest 😢
          </p>
          <button onClick={onClose} className="modal-close-btn">
            UNDERSTOOD
          </button>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);
  const playSfx = (type) => {
    const sfx = type === 'create' ? audioCreate : audioDelete;
    sfx.current.currentTime = 0;
    sfx.current.play().catch(() => {});
  };

  const handleAiGenerate = async () => {
     const user = auth.currentUser;
      if (!user) return alert("Please log in first!");
       await user.getIdToken(true);
    magicAudio.current.currentTime = 0;
  magicAudio.current.play().catch(() => {});
  setIsGenerating(true);
  try {
    const functions = getFunctions();
    const genBucket = httpsCallable(functions, 'generateBucketItems');
    const result =await genBucket({
  userId: auth.currentUser.uid
});
    const normalized = Array.isArray(result.data)
  ? result.data.map(str => {
      const [task, signal] = str.split(' — ');
      return {
        task: task?.trim(),
        signal: signal?.trim() || "DREAM"
      };
    })
  : [];

setAiSuggestions(normalized);
    playSfx('create');
  } catch (err) {
    console.error(err);
  const isLimitError =
    err.code === 'resource-exhausted' ||
    err.details?.includes('LIMIT_REACHED') ||
    err.message?.includes('LIMIT_REACHED');

  if (isLimitError) {
    if (window.navigator.vibrate) {
      window.navigator.vibrate([100, 50, 100]);
    }
    setShowLimitModal(true);
  } else {
    console.error("Unexpected AI error:", err);}
  } finally {
    setIsGenerating(false);
  }
};
const acceptAiItem = async (item, index) => {
  playSfx('create');

  await addDoc(collection(db, 'users', auth.currentUser.uid, 'bucketItems'), {
    task: item.task,
    completed: false,
    metadata: { signal: item.signal },
    createdAt: serverTimestamp()
  });

  setAiSuggestions(prev => prev.filter((_, i) => i !== index));
};
  useEffect(() => {
    if (!auth.currentUser) return;
    const q = query(collection(db, 'users', auth.currentUser.uid, 'bucketItems'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, []);
  const startEdit = (e, item) => {
  e.stopPropagation(); // Prevents the "completed" toggle
  setEditingId(item.id);
  setEditText(item.task);
};

// Handle saving the update
const saveEdit = async (itemId) => {
  if (!editText.trim()) return;
  const itemRef = doc(db, 'users', auth.currentUser.uid, 'bucketItems', itemId);
  await updateDoc(itemRef, { task: editText });
  setEditingId(null);
  playSfx('create'); // Reuse create sound for feedback
};

  const addItem = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    playSfx('create');
    await addDoc(collection(db, 'users', auth.currentUser.uid, 'bucketItems'), {
      task: input,
      completed: false,
      metadata: { signal: selectedSignal },
      createdAt: serverTimestamp()
    });
    setInput('');
  };
// Function to toggle the completed status in Firestore
const toggleComplete = async (item) => {
  try {
    const itemRef = doc(db, 'users', auth.currentUser.uid, 'bucketItems', item.id);
    await updateDoc(itemRef, { 
      completed: !item.completed 
    });
  } catch (err) {
    console.error("Toggle failed:", err);
  }
};
  return (
    <div className="bucket-page-v2">
      <div className="soft-glow-bg" />
      
      <header className="bucket-header">
        <h1 className="soft-title">2026 <span>BucketList</span></h1>
        
        <div className="hope-module single-line">
  <div className="signal-pills-row">
    {SIGNALS.map(s => (
      <button 
        key={s} 
        className={selectedSignal === s ? 'pill active' : 'pill'} 
        onClick={() => setSelectedSignal(s)}
      >
        {s}
      </button>
    ))}
  </div>

         <form onSubmit={addItem} className="soft-input-bar-horizontal">
    <div className="prefix-tag-compact">
      <AnimatePresence mode="wait">
        <motion.span 
          key={selectedSignal}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 10 }}
          className="tag-text-mini"
        >
          {selectedSignal}
        </motion.span>
      </AnimatePresence>
    </div>
    <input 
      value={input} 
      onChange={(e) => setInput(e.target.value)} 
      placeholder="What's next?"
    />
    <button type="submit" className="add-btn-compact">Add</button>
    
  </form>
</div>
      </header>
      <div className="ai-shelf-container">
  <button 
    onClick={handleAiGenerate} 
    disabled={isGenerating}
    className="ai-channel-btn"
  >
    {isGenerating ? "CHANNELING..." : "✦ AI PREDICTIONS"}
  </button>

  <div className="ai-suggestions-row">
    <AnimatePresence>
      {aiSuggestions.map((s, idx) => (
        <motion.div 
          key={idx}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.5 }}
          className="ai-suggestion-card"
        >
          <span className={`mini-sig ${s.signal}`}>{s.signal}</span>
          <p>{s.task}</p>
          <div className="ai-card-actions">
            <button onClick={() => acceptAiItem(s, idx)}>✓</button>
            <button onClick={() => setAiSuggestions(prev => prev.filter((_, i) => i !== idx))}>✕</button>
          </div>
        </motion.div>
      ))}
    </AnimatePresence>
  </div>
</div>

      <div className="hope-grid-rows">
        <AnimatePresence mode="popLayout">
          {items.map((item) => (
 <motion.div 
        layout
        key={item.id}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        /* 🌟 Removed all drag props for a static, clean box feel */
        className={`hope-row-card-box ${item.completed ? 'done' : ''}`} 
        onClick={() => editingId !== item.id && toggleComplete(item)}
      >
    <div className="card-left">
     <span className={`sig-tag ${item.metadata?.signal}`}>{item.metadata?.signal}</span>
      
      {editingId === item.id ? (
        <input 
          className="edit-input-box"
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          autoFocus
          onBlur={() => saveEdit(item.id)} // Save on click-away
          onKeyDown={(e) => e.key === 'Enter' && saveEdit(item.id)}
        />
      ) : (
        <p className="task-text-box">{item.task}</p>
      )}
    </div>

    <div className="card-actions">
      {editingId !== item.id && (
        <button className="edit-btn-tiny" onClick={(e) => startEdit(e, item)}>✎</button>
      )}
      <button className="del-btn-mini" onClick={(e) => {
        e.stopPropagation();
        playSfx('delete');
        deleteDoc(doc(db, 'users', auth.currentUser.uid, 'bucketItems', item.id));
      }}>✕</button>
    </div>
  </motion.div>
))}
        </AnimatePresence>
      </div>
       <LimitModal 
  isOpen={showLimitModal} 
  onClose={() => setShowLimitModal(false)} 
/>
    </div>
  );
}